// Arena results screen (lane C, backlog C-8; territory-ui.md §4): a black α 0.8 overlay that
// appears instantly (no fade) with our wordmark, the Continue button and four stat rows
// (Outcome, Money, Max land with a "new best" tag, Time · Kills) in two columns, labels right-
// aligned and values left-aligned. Each row fades in over 2 s on the CSS `ease` curve, staggered
// 1 s; Continue fades 4 → 6 s and becomes clickable at opacity 0.5 (4.59 s). A click, tap or
// Enter after 1 s completes every fade and enables Continue. Practice adds "Play again". Each row
// start plays `reveal` (the fourth at 4/3 rate). Timings are the hud-model.ts timeline, driven
// by `update(now)` from the app's frame loop.

import { ARENA_CLIENT } from '@ballio/shared';
import { createWordmark } from '../ui/brand.js';
import { t } from '../ui/i18n/t.js';
import {
  type ArenaResultView,
  RESULTS_TIMING,
  resultsCanSkip,
  resultsContinueEnabled,
  resultsContinueOpacity,
  resultsRowOpacity,
} from './hud-model.js';

export interface ArenaResultsDeps {
  onContinue(): void;
  /** Practice: play again in the practice arena. */
  onAgain(): void;
  /** Row `i` (0–3) started fading in (the `reveal` sound). */
  onReveal?(i: number): void;
  playClick?(): void;
}

export class ArenaResults {
  readonly el: HTMLElement;
  private readonly rows: { row: HTMLElement; value: HTMLElement; extra: HTMLElement }[] = [];
  private readonly cont: HTMLButtonElement;
  private readonly again: HTMLButtonElement;
  private readonly deps: ArenaResultsDeps;
  private openedAtMs = Number.NaN;
  private skippedAtMs: number | null = null;
  private revealed = 0;
  visible = false;

  constructor(doc: Document, deps: ArenaResultsDeps) {
    this.deps = deps;
    const root = doc.createElement('div');
    root.className = 'arena-results';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    const inner = doc.createElement('div');
    inner.className = 'arena-results-inner';
    const wm = createWordmark();
    wm.el.classList.add('arena-results-wordmark');
    const buttons = doc.createElement('div');
    buttons.className = 'arena-results-buttons';
    this.cont = doc.createElement('button');
    this.cont.type = 'button';
    this.cont.className = 'btn btn-play arena-results-continue';
    this.cont.textContent = t('arena.ui.continue');
    this.cont.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.tryContinue(performance.now());
    });
    this.again = doc.createElement('button');
    this.again.type = 'button';
    this.again.className = 'btn arena-results-again';
    this.again.textContent = t('arena.ui.again');
    this.again.hidden = true;
    this.again.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (!this.enabled(performance.now())) return;
      this.deps.playClick?.();
      this.deps.onAgain();
    });
    buttons.append(this.cont, this.again);
    const table = doc.createElement('dl');
    table.className = 'arena-results-stats';
    const labels = [
      t('arena.result.outcome'),
      t('arena.result.money'),
      t('arena.result.maxLand'),
      t('arena.result.timeKillsLabel'),
    ];
    for (const label of labels) {
      const row = doc.createElement('div');
      row.className = 'arena-results-row';
      const dt = doc.createElement('dt');
      dt.textContent = label;
      const dd = doc.createElement('dd');
      const value = doc.createElement('span');
      const extra = doc.createElement('span');
      extra.className = 'arena-results-new';
      extra.hidden = true;
      dd.append(extra, value);
      row.append(dt, dd);
      table.append(row);
      this.rows.push({ row, value, extra });
    }
    inner.append(wm.el, buttons, table);
    root.append(inner);
    root.addEventListener('click', () => this.skip(performance.now()));
    this.el = root;
  }

  show(view: ArenaResultView, practice: boolean, nowMs: number): void {
    const values = [view.outcome, view.money ?? '—', view.maxLand, view.timeKills];
    values.forEach((v, i) => {
      const r = this.rows[i];
      if (!r) return;
      r.value.textContent = v;
      r.row.style.opacity = '0';
    });
    const best = this.rows[2];
    if (best) {
      best.extra.hidden = !view.newBest;
      best.extra.textContent = view.newBest ? `${t('arena.result.newBest')} ` : '';
    }
    this.again.hidden = !practice;
    this.openedAtMs = nowMs;
    this.skippedAtMs = null;
    this.revealed = 0;
    this.visible = true;
    this.el.hidden = false;
    this.update(nowMs);
  }

  hide(): void {
    this.visible = false;
    this.el.hidden = true;
  }

  private enabled(nowMs: number): boolean {
    return resultsContinueEnabled(nowMs - this.openedAtMs, this.skippedAtMs);
  }

  /** A click / tap / Enter: skips the fades after 1 s, or continues once enabled. */
  skip(nowMs: number): void {
    if (!this.visible) return;
    const ms = nowMs - this.openedAtMs;
    if (this.skippedAtMs === null && resultsCanSkip(ms) && !this.enabled(nowMs)) {
      this.skippedAtMs = ms;
      this.update(nowMs);
    }
  }

  /** Continue (button, Enter, Esc): only once enabled; else it counts as a skip. */
  tryContinue(nowMs: number): boolean {
    if (!this.visible) return false;
    if (!this.enabled(nowMs)) {
      this.skip(nowMs);
      return false;
    }
    this.deps.playClick?.();
    this.deps.onContinue();
    return true;
  }

  update(nowMs: number): void {
    if (!this.visible) return;
    const ms = nowMs - this.openedAtMs;
    for (let i = 0; i < this.rows.length; i++) {
      const r = this.rows[i] as { row: HTMLElement };
      r.row.style.opacity = resultsRowOpacity(i, ms, this.skippedAtMs).toFixed(3);
    }
    while (
      this.revealed < RESULTS_TIMING.rows &&
      ms >= this.revealed * RESULTS_TIMING.rowStaggerMs
    ) {
      const startMs = this.revealed * RESULTS_TIMING.rowStaggerMs;
      // A row whose reveal time passed long ago (the tab was hidden, a long pause) appears
      // silently: no burst of stacked `reveal` cues on return (C-23).
      if (
        (this.skippedAtMs === null || startMs <= this.skippedAtMs) &&
        ms - startMs <= ARENA_CLIENT.staleEventMs
      ) {
        this.deps.onReveal?.(this.revealed);
      }
      this.revealed++;
    }
    const c = resultsContinueOpacity(ms, this.skippedAtMs);
    this.cont.style.opacity = c.toFixed(3);
    this.again.style.opacity = c.toFixed(3);
    const on = this.enabled(nowMs);
    this.cont.classList.toggle('is-locked', !on);
    this.cont.setAttribute('aria-disabled', on ? 'false' : 'true');
  }
}
